import React, { useState } from 'react';
import { 
  Card, 
  Button, 
  Space, 
  Typography, 
  message,
  Badge,
  Timeline,
  Radio,
  Spin
} from 'antd';
import { 
  ClockCircleOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  QrcodeOutlined,

} from '@ant-design/icons';

const { Title, Text } = Typography;

interface LedgerProgressProps {
  onSuccess: () => void;
  onReauthorize: () => void;
}

interface ProgressStatus {
  status: 'pending' | 'reviewing' | 'supplement' | 'approved' | 'rejected';
  currentStep: number;
  message: string;
  estimatedTime?: string;
  supplementMaterials?: string[];
  rejectReason?: string;
  lastUpdate: string;
  progressHistory: Array<{
    time: string;
    action: string;
    status: string;
    description?: string;
  }>;
}

const LedgerProgress: React.FC<LedgerProgressProps> = ({ onSuccess, onReauthorize }) => {
  const [progress, setProgress] = useState<ProgressStatus>({
    status: 'reviewing',
    currentStep: 1,
    message: '登记簿开立申请已提交，正在审核中...',
    estimatedTime: '预计1小时，具体以银行审核为准',
    lastUpdate: new Date().toISOString(),
    progressHistory: [
      {
        time: new Date().toISOString(),
        action: '申请提交',
        status: '已完成',
        description: '会员信息提交成功'
      },
      {
        time: new Date().toISOString(),
        action: '授权验证',
        status: '已完成',
        description: '二维码扫码授权完成'
      },
      {
        time: new Date().toISOString(),
        action: '审核处理',
        status: '进行中',
        description: '银行系统正在审核登记簿开立申请'
      }
    ]
  });
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<'reviewing' | 'approved' | 'rejected'>('reviewing');
  const [failureReasonsSelected, setFailureReasonsSelected] = useState<string[]>([]);
  const failureReasons = [
    '企业信息与工商登记不符',
    '法人身份信息验证失败',
    '企业经营状态异常或已注销',
    '企业存在不良信用记录',
    '提交的材料不清晰或信息不完整',
    '企业注册资本不符合要求'
  ];



  // 查询进度（对齐会员开立进度的三种分支）
  const queryProgress = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      if (selectedStatus === 'reviewing') {
        // 审核中：本页内刷新，不跳转
        const refreshed: ProgressStatus = {
          ...progress,
          status: 'reviewing',
          message: '登记簿开立申请正在审核中...',
          lastUpdate: new Date().toISOString(),
          progressHistory: progress.progressHistory
            .map(h => h.action === '审核处理' ? { ...h, status: '进行中' } : h)
            .filter(h => h.action !== '申请提交')
        };
        setProgress(refreshed);
        message.info('已为您刷新审核进度');
        setLoading(false);
        return;
      }
      if (selectedStatus === 'approved') {
        setLoading(false);
        message.success('登记簿开立成功，请继续绑定银行卡');
        // 成功：引导去绑卡
        onSuccess();
        return;
      }
      if (selectedStatus === 'rejected') {
        // 随机选择 2-3 条失败原因
        const pool = [...failureReasons];
        const count = Math.min(3, Math.max(2, Math.floor(Math.random() * 3) + 1));
        const picked: string[] = [];
        while (picked.length < count && pool.length > 0) {
          const idx = Math.floor(Math.random() * pool.length);
          picked.push(pool.splice(idx, 1)[0]);
        }
        setFailureReasonsSelected(picked);

        // 将“审核处理”设为已完成，并追加“开立结果通知”节点
        const historyCompletedReview = progress.progressHistory.map(h => 
          h.action === '审核处理' ? { ...h, status: '已完成' } : h
        ).filter(h => h.action !== '申请提交'); // 去掉会员提交节点

        const newStatus: ProgressStatus = {
          status: 'rejected',
          currentStep: 1,
          message: '登记簿开立申请未通过',
          lastUpdate: new Date().toISOString(),
          progressHistory: [
            ...historyCompletedReview,
            {
              time: new Date().toISOString(),
              action: '开立结果通知',
              status: '已拒绝',
              description: '审核未通过'
            }
          ]
        };
        setProgress(newStatus);
        setLoading(false);
        return;
      }
    } catch (e) {
      setLoading(false);
      message.error('查询进度失败，请重试');
    }
  };





  // 状态图标和颜色映射
  const statusConfig = {
    pending: { icon: <ClockCircleOutlined />, color: '#1890ff', text: '待处理' },
    reviewing: { icon: <ClockCircleOutlined />, color: '#1890ff', text: '审核中' },
    supplement: { icon: <ExclamationCircleOutlined />, color: '#faad14', text: '需补充材料' },
    approved: { icon: <CheckCircleOutlined />, color: '#52c41a', text: '审核通过' },
    rejected: { icon: <CloseCircleOutlined />, color: '#ff4d4f', text: '审核拒绝' }
  };

  const currentStatus = statusConfig[progress.status as keyof typeof statusConfig];

  return (
    <div style={{ maxWidth: 800, margin: '24px auto 0 auto' }}>
      <Card>
        <Spin spinning={loading}>
        {/* 演示状态切换（与会员进度一致） */}
        <div style={{ position: 'fixed', top: 200, right: 20, zIndex: 1000, background: 'rgba(255, 255, 255, 0.9)', padding: '12px 16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)', border: '1px solid #d9d9d9' }}>
          <div style={{marginBottom: 8, fontWeight: 600}}>状态切换（演示）</div>
          <Radio.Group
            value={selectedStatus}
            onChange={(e: any) => setSelectedStatus(e.target.value)}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="reviewing">📊 审核中</Radio.Button>
            <Radio.Button value="approved">✅ 开户成功</Radio.Button>
            <Radio.Button value="rejected">❌ 开户失败</Radio.Button>
          </Radio.Group>
        </div>
        {/* 状态概览 */}
        <div style={{ textAlign: 'center', margin: '64px 0 32px' }}>
          <Badge 
            count={currentStatus.text}
            style={{ 
              backgroundColor: currentStatus.color,
              fontSize: '14px',
              padding: '4px 12px'
            }}
          >
            <div style={{ fontSize: '48px', color: currentStatus.color, marginBottom: 16 }}>
              {currentStatus.icon}
            </div>
          </Badge>
          <Title level={3} style={{ marginBottom: 8, color: progress.status === 'rejected' ? '#ff4d4f' : undefined }}>
            {progress.message}
          </Title>
          {progress.estimatedTime && (
            <Text type="secondary">{progress.estimatedTime}</Text>
          )}
        </div>

        {/* 进度时间线 */}
        <Timeline style={{ marginBottom: 32 }}>
          {progress.progressHistory
            .filter(item => item.action !== '申请提交')
            .map((item, index) => (
            <Timeline.Item 
              key={index}
              dot={
                item.status === '已完成' || item.status === '已通过' ? 
                  <CheckCircleOutlined style={{ color: '#52c41a' }} /> :
                item.status === '进行中' ? 
                  <ClockCircleOutlined style={{ color: '#1890ff' }} /> :
                item.status === '需补充' ? 
                  <ExclamationCircleOutlined style={{ color: '#faad14' }} /> :
                  <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
              }
            >
              <Space direction="vertical" size={0}>
                <Text strong>{item.action}</Text>
                <Text type="secondary">{item.description}</Text>
                
              </Space>

              {/* 失败场景下在“开立结果通知”节点展示失败原因 */}
              {progress.status === 'rejected' && item.action === '开立结果通知' && (
                <div style={{ marginTop: 8 }}>
                  <Text type="danger" style={{ display: 'block', marginBottom: 4 }}>失败原因：</Text>
                  <ul style={{ paddingLeft: 18, margin: 0 }}>
                    {(failureReasonsSelected.length ? failureReasonsSelected : ['系统判定未通过']).map((r, i) => (
                      <li key={i} style={{ color: '#ff4d4f' }}>{r}</li>
                    ))}
                  </ul>
                </div>
              )}
            </Timeline.Item>
          ))}
        </Timeline>



        {/* 操作按钮区域 */}
        <div style={{ textAlign: 'center' }}>
          <Space wrap>
            {/* 查询进度按钮 - 失败状态隐藏 */}
            {progress.status !== 'rejected' && (
              <Button 
                type="primary" 
                icon={<ReloadOutlined />}
                onClick={queryProgress}
                loading={loading}
                size="large"
              >
                查询进度
              </Button>
            )}

            {/* 返回订单中心 - 审核中或失败显示，打开新标签页 */}
            {(progress.status === 'reviewing' || progress.status === 'rejected') && (
              <Button 
                size="large"
                onClick={() => window.open('about:blank', '_blank')}
              >
                返回订单中心
              </Button>
            )}

            {/* 重新扫码授权 - 审核中或失败可用 */}
            {(progress.status === 'reviewing' || progress.status === 'rejected') && (
              <Button 
                icon={<QrcodeOutlined />}
                onClick={onReauthorize}
                size="large"
              >
                重新扫码授权
              </Button>
            )}

            {/* 审核通过后的继续绑卡 */}
            {progress.status === 'approved' && (
              <Button 
                type="primary" 
                size="large" 
                onClick={onSuccess}
              >
                继续绑卡操作
              </Button>
            )}
          </Space>

          
        </div>
        </Spin>
      </Card>


    </div>
  );
};

export default LedgerProgress;